/**
 * Server time synchronization utilities.
 *
 * This module handles the offset between client and server clocks.
 * The offset is calculated during API initialization and used to generate
 * timestamps that are synchronized with the server's clock.
 *
 * The offset is stored on `window` to avoid issues with module duplication
 * across multiple bundles.
 */

declare global {
  interface Window {
    __serverTimeOffsetMs?: number;
  }
}

function getOffset(): number {
  return window.__serverTimeOffsetMs ?? 0;
}

/**
 * Updates the server time offset based on the server's reported time and request RTT.
 *
 * @param serverTimeUtc - The server's UTC time as an ISO string
 * @param requestStartMs - Timestamp when the request was initiated (Date.now())
 * @param requestEndMs - Timestamp when the response was received (Date.now())
 */
export function updateServerTimeOffset(
  serverTimeUtc: string,
  requestStartMs: number,
  requestEndMs: number,
): void {
  const serverTime = new Date(serverTimeUtc).getTime();
  const rtt = requestEndMs - requestStartMs;
  // Estimate the server time at the midpoint of the request
  const estimatedClientTimeAtServerResponse = requestStartMs + rtt / 2;
  window.__serverTimeOffsetMs =
    serverTime - estimatedClientTimeAtServerResponse;
}

/**
 * Returns the current server time offset in milliseconds.
 * Positive means server is ahead of client, negative means server is behind.
 */
export function getServerTimeOffsetMs(): number {
  return getOffset();
}

/**
 * Returns a Date object representing the current time synchronized with the server.
 * Use this instead of `new Date()` when generating timestamps for server submissions.
 */
export function getServerNow(): Date {
  // eslint-disable-next-line no-restricted-syntax
  return new Date(Date.now() + getOffset());
}

/**
 * Returns the current server-synchronized time as an ISO string.
 * Use this instead of `new Date().toISOString()` for server submissions.
 */
export function getServerNowISO(): string {
  return getServerNow().toISOString();
}

/**
 * Returns the current server-synchronized date as an ISO date string (YYYY-MM-DD).
 * Use this instead of date-only extractions from `new Date()` for server submissions.
 */
export function getServerTodayISO(): string {
  return getServerNow().toISOString().split("T")[0];
}
